import React, { useState, useEffect } from 'react';
import { X, Phone, Mail, MapPin, Home, CalendarCheck, Euro, Clock, Ban, FileText, Camera, StickyNote, Send, Tag, Receipt } from 'lucide-react';
import ClientInvoiceModal from './ClientInvoiceModal';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const absUrl = (u) => (u && u.startsWith('/') ? `${BACKEND}${u}` : u);
const eur = (n) => `€${Number(n || 0).toFixed(2)}`;
const STATUS = {
  completed: 'bg-green-100 text-green-700', cancelled: 'bg-red-100 text-red-600', in_progress: 'bg-blue-100 text-blue-700',
  scheduled: 'bg-gray-100 text-gray-700', pending: 'bg-yellow-100 text-yellow-700', confirmed: 'bg-emerald-100 text-emerald-700',
};

const Stat = ({ icon: Icon, label, value, tone = 'text-gray-900', testId }) => (
  <div className="bg-gray-50 rounded-xl p-3" data-testid={testId}>
    <p className="text-[11px] text-gray-500 flex items-center gap-1"><Icon size={12} /> {label}</p>
    <p className={`text-lg font-bold ${tone}`}>{value}</p>
  </div>
);

const ClientProfileModal = ({ client, token, language, onClose, onNewAppointment }) => {
  const pt = language === 'pt';
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [lightbox, setLightbox] = useState(null);
  const [showInvoice, setShowInvoice] = useState(false);

  useEffect(() => {
    if (!client) return;
    setData(null); setError('');
    axios.get(`${API}/admin/client-profile`, { params: { phone: client.phone || '', email: client.email || '' }, headers: { Authorization: `Bearer ${token}` } })
      .then(r => setData(r.data))
      .catch(() => setError(pt ? 'Não foi possível carregar a ficha.' : 'Could not load profile.'));
  }, [client, token, pt]);

  if (!client) return null;
  const p = data?.profile || client;
  const s = data?.stats;
  const fmt = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString(pt ? 'pt-PT' : 'en-IE', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-2 sm:p-4" data-testid="client-profile-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="bg-[#1a5c3a] text-white px-5 py-4 rounded-t-2xl flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wide text-white/70">{pt ? 'Ficha do cliente' : 'Client profile'}</p>
            <h2 className="text-xl font-semibold truncate flex items-center gap-2" data-testid="client-profile-name">{p.name}
              {p.is_airbnb && <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-500 text-white uppercase">Airbnb</span>}
            </h2>
            <div className="text-sm text-white/85 flex flex-wrap gap-x-4 gap-y-1 mt-1">
              {p.phone && <a href={`https://wa.me/${String(p.phone).replace(/\D/g, '').replace(/^0/, '353')}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:underline" data-testid="client-profile-whatsapp"><Phone size={13} /> {p.phone}</a>}
              {p.email && p.email !== 'sem-email@sancur.ie' && <span className="inline-flex items-center gap-1"><Mail size={13} /> {p.email}</span>}
              {(p.address || p.eircode) && <span className="inline-flex items-center gap-1"><MapPin size={13} /> {p.address} {p.eircode}</span>}
              {(p.bedrooms || p.property_type) && <span className="inline-flex items-center gap-1"><Home size={13} /> {p.property_type || ''} {p.bedrooms ? `· ${p.bedrooms}Q` : ''} {p.bathrooms ? `· ${p.bathrooms}WC` : ''} {p.has_pets ? '· 🐾' : ''}</span>}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button onClick={() => setShowInvoice(true)} data-testid="client-profile-invoice" className="inline-flex items-center gap-1 text-xs bg-white text-[#1a5c3a] hover:bg-emerald-50 px-3 py-1.5 rounded-full font-semibold"><Receipt size={14} /> {pt ? 'Fatura' : 'Invoice'}</button>
            {onNewAppointment && <button onClick={() => onNewAppointment(p)} data-testid="client-profile-new-appointment" className="hidden sm:inline-flex items-center gap-1 text-xs bg-white/15 hover:bg-white/25 px-3 py-1.5 rounded-full"><CalendarCheck size={14} /> {pt ? 'Novo agendamento' : 'New appointment'}</button>}
            <button onClick={onClose} className="p-1.5 rounded-full hover:bg-white/15" data-testid="client-profile-close"><X size={20} /></button>
          </div>
        </div>

        <div className="p-5 overflow-y-auto space-y-5">
          {error && <p className="text-sm text-red-600">{error}</p>}
          {!data && !error && <p className="text-sm text-gray-500">{pt ? 'A carregar…' : 'Loading…'}</p>}
          {s && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="client-profile-stats">
              <Stat icon={CalendarCheck} label={pt ? 'Serviços concluídos' : 'Completed jobs'} value={`${s.completed}/${s.total_appointments}`} testId="stat-completed" />
              <Stat icon={Euro} label={pt ? 'Total faturado' : 'Total billed'} value={eur(s.total_spent)} tone="text-[#1a5c3a]" testId="stat-spent" />
              <Stat icon={Clock} label={pt ? 'Horas trabalhadas' : 'Hours worked'} value={`${s.total_hours}h`} testId="stat-hours" />
              <Stat icon={Receipt} label={pt ? 'Em dívida' : 'Amount due'} value={eur(s.total_due)} tone={s.total_due > 0 ? 'text-red-600' : 'text-green-600'} testId="stat-due" />
              <Stat icon={CalendarCheck} label={pt ? 'Próximos' : 'Upcoming'} value={s.upcoming} tone="text-blue-600" testId="stat-upcoming" />
              <Stat icon={Ban} label={pt ? 'Cancelados' : 'Cancelled'} value={s.cancelled} tone="text-red-600" testId="stat-cancelled" />
              <Stat icon={FileText} label={pt ? 'Orçamentos' : 'Quotes'} value={s.quotes} testId="stat-quotes" />
              <Stat icon={Send} label={pt ? 'Cliente desde' : 'Client since'} value={fmt(s.first_date)} testId="stat-since" />
            </div>
          )}

          {data && (
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">{pt ? 'Histórico' : 'History'} <span className="text-xs text-gray-400 font-normal">({data.history.length})</span></h3>
              <div className="space-y-2" data-testid="client-profile-history">
                {data.history.length === 0 && <p className="text-sm text-gray-500">{pt ? 'Sem registos.' : 'No records.'}</p>}
                {data.history.map(h => (
                  <div key={`${h.kind}-${h.id}`} className={`border rounded-xl p-3 ${h.source === 'airbnb' ? 'border-l-4 border-l-rose-500' : ''}`} data-testid={`history-item-${h.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900">
                          {fmt(h.date)} {h.time ? `· ${h.time}` : ''} · {h.service_type || '-'}
                          {h.kind === 'quote' && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 uppercase">{pt ? 'Orçamento' : 'Quote'}</span>}
                        </p>
                        <p className="text-xs text-gray-500 flex flex-wrap gap-x-3">
                          {h.staff?.length > 0 && <span>👤 {h.staff.join(', ')}</span>}
                          {h.time_worked_seconds > 0 && <span>⏱ {(h.time_worked_seconds / 3600).toFixed(1)}h</span>}
                          {h.address && <span className="truncate">📍 {h.address}</span>}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <span className={`text-[11px] px-2 py-0.5 rounded-full ${STATUS[h.status] || 'bg-gray-100 text-gray-600'}`}>{h.status}</span>
                        {h.kind === 'appointment' && h.status === 'completed' && (
                          <span className={`ml-1 text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${h.paid ? 'bg-green-100 text-green-700' : 'bg-red-50 text-red-600'}`}>{h.paid ? (pt ? 'PAGO' : 'PAID') : (pt ? 'EM DÍVIDA' : 'DUE')}</span>
                        )}
                        {h.price != null && (
                          <p className="text-sm font-semibold text-gray-900 mt-1">
                            {h.discount_amount > 0 && <span className="text-xs text-gray-400 line-through mr-1">{eur(h.original_price || Number(h.price) + Number(h.discount_amount))}</span>}
                            {eur(h.price)}
                          </p>
                        )}
                      </div>
                    </div>
                    {(h.notes || h.staff_notes) && (
                      <div className="mt-2 text-xs text-gray-700 space-y-1">
                        {h.notes && <p className="flex items-start gap-1"><StickyNote size={12} className="mt-0.5 text-gray-400 flex-shrink-0" /> <span>{h.notes}</span></p>}
                        {h.staff_notes && <p className="flex items-start gap-1 bg-blue-50 rounded-lg p-2"><StickyNote size={12} className="mt-0.5 text-blue-400 flex-shrink-0" /> <span><b>{pt ? 'Staff' : 'Staff'}:</b> {h.staff_notes}</span></p>}
                      </div>
                    )}
                    {h.photos?.length > 0 && (
                      <div className="mt-2 flex gap-1.5 flex-wrap" data-testid="history-photos">
                        {h.photos.map((u, i) => (
                          <button key={i} type="button" onClick={() => setLightbox(absUrl(u))} className="w-14 h-14 rounded-lg overflow-hidden border hover:ring-2 hover:ring-[#1a5c3a]">
                            <img src={absUrl(u)} alt="" className="w-full h-full object-cover" />
                          </button>
                        ))}
                        <span className="self-center text-[11px] text-gray-400 inline-flex items-center gap-1"><Camera size={11} /> {h.photos.length}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      {showInvoice && <ClientInvoiceModal client={{ name: p.name, phone: p.phone, email: p.email }} token={token} language={language} onClose={() => setShowInvoice(false)} />}
      {lightbox && (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4" onClick={(e) => { e.stopPropagation(); setLightbox(null); }}>
          <img src={lightbox} alt="" className="max-w-full max-h-full rounded-xl" />
        </div>
      )}
    </div>
  );
};

export default ClientProfileModal;
